import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import Reveal from "./Reveal";
import { IMAGES, BRAND, COACH_IMAGES } from "../../data/brand";

const STATS = [
  { num: "10+", label: "Years Personal Training" },
  { num: "8", label: "Years Certified Holistic Nutrition" },
];

function CoachCarousel() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(
      () => setIdx((i) => (i + 1) % COACH_IMAGES.length),
      5000
    );
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative" data-testid="coach-carousel">
      <div className="absolute -inset-3 border border-[#d4af37]/30 z-0" />
      <div className="relative z-10 w-full aspect-[4/5] overflow-hidden bg-[#0a0a0a]">
        <AnimatePresence mode="sync">
          <motion.img
            key={idx}
            src={COACH_IMAGES[idx]}
            alt="Coach Tristian"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        </AnimatePresence>
      </div>

      {/* Dots */}
      <div className="absolute bottom-4 left-4 z-30 flex gap-2">
        {COACH_IMAGES.map((_, i) => (
          <button
            key={i}
            type="button"
            data-testid={`coach-dot-${i}`}
            onClick={() => setIdx(i)}
            aria-label={`Show photo ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-300 ${
              idx === i ? "w-6 bg-[#d4af37]" : "w-2 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>

      {/* Nutrition inset */}
      <motion.img
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.3 }}
        src={IMAGES.nutrition}
        alt="Holistic nutrition"
        className="absolute -bottom-8 -right-4 sm:-right-8 z-20 w-28 h-28 sm:w-40 sm:h-40 object-cover border-4 border-[#0a0a0a]"
      />
    </div>
  );
}

export default function About() {
  return (
    <section
      id="coach"
      data-testid="about-section"
      className="relative py-28 sm:py-40 px-5 sm:px-8 bg-[#0a0a0a] border-y border-white/5"
    >
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Image column */}
        <div className="lg:col-span-5 relative">
          <Reveal>
            <CoachCarousel />
          </Reveal>
        </div>

        {/* Text column */}
        <div className="lg:col-span-7 lg:pl-10">
          <Reveal delay={0.15}>
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#d4af37] mb-6">
              Your Coach
            </p>
            <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-8">
              Meet {BRAND.coach}
            </h2>
            <p className="text-lg font-light text-neutral-300 leading-relaxed max-w-xl mb-6">
              A rare dual-threat: over a decade of hands-on fitness coaching
              paired with eight years as a certified holistic nutritionist. That
              means your training isn&apos;t just about making you sweat — it&apos;s
              a complete, systemic approach to how you move, fuel, and recover.
            </p>
            <p className="text-lg font-light text-neutral-400 leading-relaxed max-w-xl mb-12">
              Health is built, not chased. Tristian teaches you the &ldquo;why&rdquo;
              behind every movement and habit, so the results outlast the fifteen days.
            </p>

            <div className="grid grid-cols-2 gap-8 border-t border-white/10 pt-10">
              {STATS.map((s) => (
                <div key={s.label}>
                  <p className="font-display font-black text-5xl sm:text-6xl gold-text leading-none mb-3">
                    {s.num}
                  </p>
                  <p className="text-sm text-neutral-400 uppercase tracking-wide">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
